import type { GameMode } from '../chart.js';
import type { Difficulty } from '../gameTypes.js';
import songData from './ncsSongs.json' with { type: 'json' };

export interface NcsSong {
  /** Stable song hub slug, independent of the per-mode chart routes. */
  baseSlug: string;
  /** Song title as it appears on the chart (no variant suffix). */
  title: string;
  /** Performing artist, parsed from the chart's attribution. */
  artist: string;
  /** Official release URL, parsed from the attribution. */
  releaseUrl: string;
  label: 'NCS' | 'Monstercat';
  /** Song blurb from the chart description (genre / energy / structure). */
  description: string;
  /** Perceived tempo in BPM (already folded into the human-tempo range). */
  displayBpm: number;
  /** Authoring difficulty 1–5, used to rank "similar tracks". */
  difficulty: Difficulty;
  /** Only modes whose actual audio matches this release. */
  playModes: Array<{ mode: GameMode; slug: string }>;
}

/** Generated by tools/BuildNcsSongCatalogue.ts; contains no gameplay chart data. */
const NCS_SONGS = songData as NcsSong[];

/** Every recording, with the established Pulse catalogue first. */
export function listNcsSongs(): NcsSong[] {
  return NCS_SONGS;
}

/** Look up a single song by its base slug, or `undefined` if unknown. */
export function getNcsSong(slug: string): NcsSong | undefined {
  return NCS_SONGS.find((song) => song.baseSlug === slug);
}

/**
 * Distance between two songs — lower means more alike. Combines tempo
 * proximity (per ~20 BPM) and difficulty proximity, with a strong pull
 * towards same-artist tracks. Purely derived from real chart properties, so
 * the "songs like X" list is an honest recommendation, not a fabricated one.
 */
function relatednessScore(a: NcsSong, b: NcsSong): number {
  const bpmDistance = Math.abs(a.displayBpm - b.displayBpm) / 20;
  const difficultyDistance = Math.abs(a.difficulty - b.difficulty) * 1.5;
  const sameArtistBonus = a.artist === b.artist ? -5 : 0;
  return bpmDistance + difficultyDistance + sameArtistBonus;
}

/**
 * The `limit` songs most similar to `slug` (by tempo, difficulty and artist).
 * Returns an empty list for an unknown slug.
 */
export function relatedNcsSongs(slug: string, limit = 3): NcsSong[] {
  const target = getNcsSong(slug);
  if (!target) {
    return [];
  }
  return NCS_SONGS.filter((song) => song.baseSlug !== slug)
    .map((song) => ({ song, score: relatednessScore(target, song) }))
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((entry) => entry.song);
}
